import { useModal } from 'modals';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useSnapshot } from 'valtio';

import {
  StripeCustomer,
  StripePlan,
  StripeProduct,
} from '@cartwhl/common/types';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import {
  authStore,
  checkoutStore,
  customerStore,
  productStore,
} from 'stores/global';

export default function PlanCard({
  mode = 'month',
  product,
  showCharacters = true,
}: {
  mode?: 'month' | 'year';
  product: StripeProduct | null;
  showCharacters?: boolean;
}) {
  const navigate = useNavigate();
  const { authStatus } = useSnapshot(authStore.state);
  const { customer } = useSnapshot(customerStore.state);
  const price = productStore.getPriceByProductAndMode(product?.id, mode);
  const subscriberPremiumStatus = customerStore.getSubscriberPremiumStatus(
    customer as any as StripeCustomer,
  );
  const isPaying = subscriberPremiumStatus === 'isPaying';

  // Determine if customer is on unlimited plan by checking their plans
  const isUnlimitedCustomer =
    isPaying &&
    customer?.plans?.some(
      (plan: StripePlan) => plan.product === getStripeProductID('unlimited'),
    );

  // Determine the current plan based on subscription status
  const currentPlan = isUnlimitedCustomer
    ? 'unlimited'
    : isPaying
      ? 'pro'
      : 'free';

  const cta = getCTA(authStatus, currentPlan, product);

  const isCreatorPlan = product?.name.includes('Creator');
  const isProPlan = product?.name.includes('Pro');
  const isUnlimitedPlan = product?.name.includes('Unlimited');

  if (!price) {
    // Show placeholder while price is loading
    return (
      <div className="bg-gray-15 dark:bg-gray-90 flex min-h-[390px] flex-1 animate-pulse flex-col flex-nowrap rounded-2xl border px-4 py-5 xl:px-6 xl:py-7">
        <div className="bg-gray-30 dark:bg-gray-70 mb-6 h-8 w-3/4 rounded" />
        <div className="mb-8">
          <div className="bg-gray-30 dark:bg-gray-70 mb-4 h-4 w-1/2 rounded" />
          <div className="space-y-2">
            <div className="bg-gray-30 dark:bg-gray-70 ml-8 h-3 w-1/2 rounded" />
            <div className="bg-gray-30 dark:bg-gray-70 ml-8 h-3 w-1/2 rounded" />
            <div className="bg-gray-30 dark:bg-gray-70 ml-8 h-3 w-1/2 rounded" />
          </div>
        </div>
        <div className="bg-gray-30 dark:bg-gray-70 mb-8 mt-auto h-16 w-3/5 rounded" />
        <div className="bg-gray-30 dark:bg-gray-70 h-10 w-full rounded" />
      </div>
    );
  }

  return (
    <CardLayout
      className={
        isUnlimitedPlan
          ? 'border-blue bg-blue/10'
          : 'bg-gray-15 border-gray-30 dark:border-gray-60 dark:bg-gray-90'
      }
      title={
        <>
          {isUnlimitedPlan && (
            <div className="bg-blue inline-block h-6 w-6 rounded-full" />
          )}
          <span>{product?.name?.replace('[T] ', '')}</span>
        </>
      }
      description={
        <>
          {isUnlimitedPlan
            ? 'Unlimited spins and all pro options'
            : isProPlan
              ? 'More spins and pro options'
              : 'Start creating motion for free'}

          <ul className="list-disc pl-5">
            {product?.features.map(({ name }, i) => <li key={i}>{name}</li>)}
          </ul>
        </>
      }
    >
      {isUnlimitedPlan && showCharacters && (
        <div className="absolute -left-2 -right-2 bottom-full hidden md:block">
          <img
            src="/images/pricing_characters.png"
            alt="Cartwheel Characters"
          />
        </div>
      )}
      <div className="flex items-end">
        <Text variant="studio/display/xl" xl:variant="studio/display/2xl">
          ${productStore.formatMoney(price?.unit_amount || 0)}
        </Text>
        {price?.recurring && (
          <Text
            variant="web/body/medium/lg"
            xl:variant="web/body/medium/xl"
            className="text-gray-45 dark:text-gray-60"
          >
            <span className="mx-1.5">/</span>
            {productStore.formatInterval(price?.recurring.interval || 'month')}
          </Text>
        )}
      </div>
      <Button
        className={`mt-8 ${isCreatorPlan || isProPlan ? 'dark:bg-gray-60 bg-black' : ''}`}
        variant={'primary'}
        disabled={cta === 'Your current plan'}
        onClick={async () => {
          // Check if user is trying to switch between paid plans (Pro to Unlimited or vice versa)
          const isSwitchingBetweenPaidPlans =
            // Current on Pro plan and clicking Unlimited plan
            (currentPlan === 'pro' && isUnlimitedPlan) ||
            // Current on Unlimited plan and clicking Pro plan
            (currentPlan === 'unlimited' && isProPlan);
          if (!isSwitchingBetweenPaidPlans && (isProPlan || isUnlimitedPlan)) {
            checkoutStore.select(price);
            await checkoutStore.startCheckout();
          } else if (authStatus !== 'anonymous') {
            const portalSession = await getStripePortalSession(
              customer?.id ?? '',
              price.id,
            );
            if (portalSession?.data) {
              window.location.href = portalSession.data.url;
            }
          } else {
            navigate('/sign-up');
          }
        }}
      >
        {cta}
      </Button>
    </CardLayout>
  );
}

function getCTA(
  authStatus: string,
  currentPlan: 'free' | 'pro' | 'unlimited',
  product?: StripeProduct | null,
) {
  if (!product) {
    return 'Loading...';
  }

  if (authStatus === 'anonymous') {
    return `Sign up for ${product.name}`;
  }

  const freeProductId = getStripeProductID('free');
  const proProductId = getStripeProductID('pro');
  const unlimitedProductId = getStripeProductID('unlimited');

  // If viewing the current plan
  if (
    (currentPlan === 'free' && product.id === freeProductId) ||
    (currentPlan === 'pro' && product.id === proProductId) ||
    (currentPlan === 'unlimited' && product.id === unlimitedProductId)
  ) {
    return 'Your current plan';
  }

  // Upgrade/downgrade logic
  if (currentPlan === 'free') {
    return `Upgrade to ${product.name}`;
  } else if (currentPlan === 'pro') {
    if (product.id === unlimitedProductId) {
      return `Upgrade to ${product.name}`;
    } else {
      return `Downgrade to ${product.name}`;
    }
  } else if (currentPlan === 'unlimited') {
    return `Downgrade to ${product.name}`;
  }
  return 'Select plan';
}

export function EnterpriseCard() {
  const { open } = useModal();

  return (
    <div
      className={`dark:border-gray-60 relative flex w-full flex-none flex-col flex-nowrap justify-between rounded-2xl border border-green-50 bg-green-50/10 px-4 py-6 sm:flex-row md:min-h-[390px] md:flex-1 md:flex-col xl:px-6 xl:py-7`}
    >
      <div className="">
        <Text
          as="h2"
          variant="web/title/lg"
          className="flex items-center gap-2"
        >
          <div className="inline-block h-6 w-6 rounded-full bg-green-50" />
          Enterprise
        </Text>
        <div className="grow pt-6">
          <Text
            as="p"
            variant="web/body/medium/md"
            className="text-gray-60 dark:text-gray-45 max-w-[245px]"
          >
            For organizations who operate with unique pipelines, require custom
            control, and have specific security needs.
          </Text>
        </div>
      </div>
      <div className="flex flex-col pt-8 sm:pt-0 md:mt-auto md:pt-8">
        <Button
          variant="primary"
          className="bg-gray-90 min-w-[191px]"
          onClick={() => open('ENTERPRISE_CONTACT', { reason: 'pricing_plans' })}
        >
          Contact us
        </Button>
      </div>
    </div>
  );
}

function CardLayout({
  title,
  description,
  children,
  className,
}: {
  title: ReactNode;
  description: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`relative flex flex-1 flex-col flex-nowrap rounded-2xl border px-4 py-5 md:min-h-[390px] xl:px-6 xl:py-7 ${className || ''}`}
    >
      <Text as="h2" variant="web/title/lg" className="flex items-center gap-2">
        {title}
      </Text>
      <div className="grow pt-6">
        <Text
          as="div"
          variant="web/body/medium/md"
          className="text-gray-60 dark:text-gray-45 max-w-[320px]"
        >
          {description}
        </Text>
      </div>
      <div className="mt-auto flex flex-col pt-8">{children}</div>
    </div>
  );
}
